import { Planning } from 'interfaces/planning.interface';
import { ModifyTask, PostTask, TaskFull } from 'interfaces/task.interface';
import { dayjs, Equipment } from '@umanforge/common';

import { Components, Views, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../../../styles/react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { Fragment } from 'react';
import { i18n } from '@umanforge/common';
import TaskModal from '../../../features/task_modal/components/taskModal.component';
import { SectorSettings } from 'interfaces/setting.interface';
import TaskModalInfo from 'features/task_modal/components/taskModalInfo.component';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import usePlanningComponent from '../hook/planning.hook';
import { CalendarEvent } from 'interfaces/calendar.interface';

import '../../../styles/react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useTranslation } from 'react-i18next';
import { RecurrenceSVG } from '@common_components/svg/recurrence.svg';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import ContextMenuItem from 'features/context_menu/components/context_menu_item.component';
import { EditBoxSVG } from '@common_components/svg/editBox.svg';
import { TrashSVG } from '@common_components/svg/trash.svg';
import { CopySVG } from 'components/svg/copy.svg';
import { Warning } from 'interfaces/warning.interface';
import { TimeGrid } from 'interfaces/timegrid.interface';

interface PlanningProps {
  planning?: Planning,
  tasks: TaskFull[],
  currentDate: dayjs.Dayjs;
  settings?: SectorSettings;
  onTaskCreate?: ( task: PostTask ) => Promise<string> | null;
  onUpdate?: ( task: ModifyTask, refreshTask?: boolean ) => Promise<boolean>;
  onDeleteTask?: ( taskid: string ) => void;
  fixed?: boolean;
  isDayView?: boolean;
  refreshTask?: () => Promise<void>;
  showOnlyMyTasks?: boolean;
  highlightMyTasks?: boolean;
  equipments: Equipment[];
  equipmentsTasks: TaskEquipment[];
  taskActionImages: Map<string, string | null>;
  onWarnings: ( absenceUsers: Warning[] ) => void
  openTaskModal?: string;
  timegrid: TimeGrid;
}

const PlanningComponent = ( {
  planning,
  tasks = [],
  currentDate,
  settings,
  onTaskCreate = ( ) => {return null; },
  onUpdate = ( ) => {return Promise.resolve( true ); },
  onDeleteTask = ( ) => {return null; },
  refreshTask = ( ) => { return Promise.resolve(); },
  fixed = false,
  isDayView = false,
  showOnlyMyTasks = false,
  highlightMyTasks = false,
  equipments = [],
  equipmentsTasks = [],
  taskActionImages,
  onWarnings = () => { /** */ },
  openTaskModal,
  timegrid
} : PlanningProps ) => {

  const {
    DnDCalendar,
    min,
    max,
    components,
    eventPropGetter,
    formats,
    step,
    timeslots,
    calendarEvents, setCurrentEvent,
    isModalOpen,
    currentEvent,
    timeStartDrag,
    timeEndDrag,
    dateDrag,
    views,
    updateTask,
    updateOnDrag,
    updateOnResize,
    handleSelectEvent,
    handleSelectSlot,
    setTimeStartDrag,
    setTimeEndDrag,
    setDateDrag,
    setIsModalOpen,
    customTimeSlot,
    myDayLayoutAlgorithm,
    confirmationRecurrenceModalOpen, setConfirmationRecurrenceModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen,
    handleDelete,
    contextMenu, setContextMenu,
    menuRef,
    duplicateTask
  } = usePlanningComponent(
    tasks,
    currentDate,
    timegrid,
    equipments,
    equipmentsTasks,
    taskActionImages,
    onUpdate,
    settings,
    fixed,
    isDayView,
    showOnlyMyTasks,
    highlightMyTasks,
    refreshTask,
    onWarnings,
    openTaskModal
  );

  const { t } = useTranslation();
  return (
    planning != null && (
      <>
        <Fragment>
          <DnDCalendar
            localizer={dayjsLocalizer( dayjs )}
            date={currentDate.startOf( 'day' ).toDate()}
            events={ calendarEvents }
            defaultView={Views.WEEK}
            views={views}
            resizable={!fixed}
            selectable={!fixed}
            toolbar={false}
            components={components as unknown as Components}
            slotPropGetter={customTimeSlot}

            step={step}
            timeslots={timeslots}
            culture={i18n.language}
            min={min}
            max={max}
            formats={formats}
            dayLayoutAlgorithm={myDayLayoutAlgorithm}
            showMultiDayTimes={false}

            onSelectEvent={handleSelectEvent}
            onSelectSlot={ event => { if ( !fixed ) { handleSelectSlot( event ); } } }
            eventPropGetter={eventPropGetter}

            onEventDrop={ fixed ? undefined : updateOnDrag}
            onEventResize={ fixed ? undefined : updateOnResize}
            tooltipAccessor={ null }

            onNavigate={() => { return null; } }
            allDayAccessor={ ( event: CalendarEvent ) => {
              if ( event.allDayIndicator ) {
                return false;
              }
              if ( event.allDay ) {
                return true;
              }
              if ( dayjs( event.start ).format( 'HH:mm' ) === '00:00' && dayjs( event.end ).format( 'HH:mm' ) === '23:59' ) {
                return true;
              }
              return false;
            } }

            draggableAccessor={ ( event: CalendarEvent )  => {
              if ( event.task?.status !== 'COMING' ) {
                return false;
              }
              if ( event.allDayIndicator && !event.draggable ) {
                return false;
              }
              return true;
            } }
            resizableAccessor={ ( event: CalendarEvent )  => {
              return !( event.allDayIndicator && !event.resizable && event.task?.status == 'COMING' );
            } }
          />
        </Fragment>

        { contextMenu && (
          <div
            className='fixed border border-gray-300 bg-gray-200 w-auto h-auto z-20 flex flex-col rounded-lg'
            style={{
              top: contextMenu.mouseY + 10,
              left: contextMenu.mouseX
            }}
            ref={menuRef}
          >
            <ContextMenuItem
              key={'context_item_edit'}
              label={ t( 'translation:edit' ) }
              icon={<EditBoxSVG className='w-4 h-4'/>}
              onClick={ () => {
                setContextMenu( null );
                if ( contextMenu.task ) {
                  setCurrentEvent( { task: contextMenu.task } as CalendarEvent );
                  setIsModalOpen( true );
                }
              } }
            />

            <ContextMenuItem
              key={'context_item_duplicate'}
              label={ t( 'translation:duplicate' ) }
              icon={<CopySVG className='w-4 h-4 '/>}
              onClick={ () => {
                duplicateTask( contextMenu.task?.id );
                setContextMenu( null );
              } }
            />

            <ContextMenuItem
              key={'context_item_delete'}
              label={ t( 'translation:delete' ) }
              icon={<TrashSVG className='w-4 h-4 '/>}
              onClick={ () => {
                setCurrentEvent( { task: contextMenu.task } as CalendarEvent );
                setIsDeleteModalOpen( true );
                setContextMenu( null );
              } }
            />
          </div>
        ) }




        { isModalOpen &&
          ( ( fixed || ( currentEvent?.task && currentEvent.task?.status != 'COMING' ) ) ?
            <TaskModalInfo
              onClose={ () => { setIsModalOpen( false ); } }
              taskId={ currentEvent?.task?.id ?? '' }
              sectorId={ planning.idSector }
              onActionUpdate={refreshTask}
            /> :
            <TaskModal
              taskId={ currentEvent?.task?.id }
              planningId={ planning.id }
              sectorId={ planning.idSector }
              onClose={ () => {
                setTimeStartDrag( undefined );
                setTimeEndDrag( undefined );
                setDateDrag( undefined );
                setIsModalOpen( false );
                refreshTask();
              } }
              onDeleteTask={ onDeleteTask }
              onTaskCreate={ onTaskCreate }
              onUpdate={ onUpdate }

              timeStartDrag={timeStartDrag}
              timeEndDrag={timeEndDrag}
              dateDrag={dateDrag}

              settings={settings}
            />
          )
        }
        { confirmationRecurrenceModalOpen &&
          <ConfirmationModal
            isOpen={confirmationRecurrenceModalOpen != null}
            onConfirm={() => {
              updateTask( confirmationRecurrenceModalOpen, true, false );
            }}
            onCancel={() => {
              updateTask( confirmationRecurrenceModalOpen, true, true );
            }}
            onClose={ () => { setConfirmationRecurrenceModalOpen( undefined ); } }
            confirmLabel={t( 'translation:task_only' )}
            confirmColor='gray'
            cancelLabel={t( 'translation:save_for_all_mission' )}
            title={ t( 'translation:task_have_recurrence' ) }
            description={ t( 'translation:do_you_want_to_move_the_task_only_or_all_the_tasks_on_the_recurrence' ) }
            icon={<RecurrenceSVG className='w-8' stroke='dark-gray'/>}
          />
        }

        <ConfirmationModal
          isOpen={ isDeleteModalOpen }
          onConfirm={ () => {
            if ( currentEvent?.task?.idRecurrence ) {
              setIsDeleteModalOpen( false );
              setIsDeleteRecurrenceModalOpen( true );
            }
            else {
              handleDelete();
              setIsDeleteRecurrenceModalOpen( false );
              setIsDeleteModalOpen( false );
            }
          } }
          onCancel={() => { return setIsDeleteModalOpen( false );}}
          onClose={ () => { return setIsDeleteModalOpen( false ); } }
          title={ t( 'translation:delete_task' ) }
          description={ t( 'translation:are_you_sure_that_you_want_to_delete_task_it_is_irreversible' ) }
        />

        <ConfirmationModal
          isOpen={isDeleteRecurrenceModalOpen}
          onConfirm={ () => {
            handleDelete();
            setIsDeleteRecurrenceModalOpen( false );
            setIsDeleteModalOpen( false );
          } }
          onCancel={() => {
            handleDelete( true );
            setIsDeleteRecurrenceModalOpen( false );
            setIsDeleteModalOpen( false );
          } }
          onClose={ () => { setIsDeleteRecurrenceModalOpen( false ); } }
          confirmLabel={t( 'translation:task_only' )}
          confirmColor='gray'
          cancelLabel={t( 'translation:save_for_all_mission' )}
          title={ t( 'translation:delete_recurrence_task' ) }
          description={ t( 'translation:do_you_want_to_delete_the_task_only_or_all_the_tasks_on_the_recurrence' ) }
          icon={<RecurrenceSVG className='w-8' stroke='dark-gray'/>}
        />
      </>
    )
  );
};

export default PlanningComponent;